<template>
  <div class="container mt-5" style="max-width: 460px;">
    <div class="card shadow-sm">
      <div class="card-body p-4">
        <h4 class="card-title text-center mb-4 fw-bold">회원가입</h4>

        <div class="mb-3">
          <label class="form-label">아이디 <span class="text-muted small">(최소 4자 / 최대 20자)</span></label>
          <input v-model="form.id" type="text" class="form-control" maxlength="20" placeholder="아이디 입력" />
          <div v-if="form.id && form.id.length < 4" class="form-text text-danger">아이디는 최소 4자 이상이어야 합니다.</div>
        </div>

        <div class="mb-3">
          <label class="form-label">비밀번호 <span class="text-muted small">(최소 4자 이상)</span></label>
          <input v-model="form.password" type="password" class="form-control" placeholder="비밀번호 입력" />
          <div v-if="form.password && form.password.length < 4" class="form-text text-danger">비밀번호는 최소 4자 이상이어야 합니다.</div>
        </div>

        <div class="mb-3">
          <label class="form-label">비밀번호 확인</label>
          <input v-model="form.passwordConfirm" type="password" class="form-control" placeholder="비밀번호 재입력" />
          <div v-if="form.passwordConfirm && form.password !== form.passwordConfirm" class="form-text text-danger">비밀번호가 일치하지 않습니다.</div>
        </div>

        <div class="mb-3">
          <label class="form-label">이름 <span class="text-muted small">(최대 50자)</span></label>
          <input v-model="form.name" type="text" class="form-control" maxlength="50" placeholder="이름 입력" />
        </div>

        <div class="mb-3">
          <label class="form-label">전화번호 <span class="text-muted small"></span></label>
          <div class="input-group">
            <input 
              v-model="form.tel" 
              type="text" 
              class="form-control" 
              maxlength="20" 
              placeholder="예: 010-1234-5678"
              :disabled="phoneVerified"
              @input="(e) => form.tel = formatPhoneDisplay(e.target.value)"
            />
            <button 
              v-if="!phoneVerified"
              class="btn btn-outline-secondary" 
              type="button" 
              @click="requestAuthCode"
              :disabled="authSending || !form.tel || form.tel.replace(/\D/g, '').length < 10"
            >
              {{ authSending ? '전송 중...' : '인증' }}
            </button>
            <button 
              v-else
              class="btn btn-success" 
              type="button" 
              disabled
            >
              ✓ 인증완료
            </button>
          </div>
          <div v-if="authError" class="form-text text-danger mt-1">{{ authError }}</div>
        </div>

        <!-- 인증번호 입력 섹션 -->
        <div v-if="showAuthInput && authSent" class="mb-3">
          <label class="form-label">인증번호 입력</label>
          <div class="input-group mb-2">
            <input 
              v-model="inputAuthCode" 
              type="text" 
              class="form-control" 
              maxlength="6" 
              placeholder="6자리 인증번호"
              @keyup.enter="verifyAuthCode"
            />
            <button 
              class="btn btn-primary" 
              type="button" 
              @click="verifyAuthCode"
            >
              인증 확인
            </button>
          </div>
          <div class="d-flex justify-content-between align-items-center">
            <small class="text-muted">
              <span v-if="authTimeRemaining > 0" class="text-danger fw-bold">
                제한시간: {{ Math.floor(authTimeRemaining / 60) }}:{{ String(authTimeRemaining % 60).padStart(2, '0') }}
              </span>
              <span v-else class="text-danger">시간 초과</span>
            </small>
            <small v-if="resendRemaining > 0" class="text-muted">재전송 대기: {{ resendRemaining }}초</small>
            <button 
              v-else-if="authTimeRemaining > 0 && !authSending"
              class="btn btn-sm btn-link p-0" 
              @click="requestAuthCode"
            >
              재전송
            </button>
          </div>
        </div>

        <div class="mb-4">
          <label class="form-label">차량번호 <span class="text-muted small"></span></label>
          <input v-model="form.carNumber" type="text" class="form-control" maxlength="20" placeholder="예: 12가3456 또는 서울12가3456" />
        </div>

        <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
        <div v-if="success" class="alert alert-success py-2">{{ success }}</div>
        <div v-if="authSuccess" class="alert alert-info py-2">✓ 본인인증이 완료되었습니다.</div>

        <button class="btn btn-dark w-100 mb-2" :disabled="loading || !phoneVerified" @click="register">
          {{ loading ? '처리 중...' : (phoneVerified ? '회원가입' : '본인인증 후 회원가입 가능') }}
        </button>
        <button class="btn btn-outline-secondary w-100" @click="$router.push('/login')">
          로그인으로 돌아가기
        </button>
      </div>
    </div>

    <!-- 최종 확인 모달 -->
    <div v-if="showConfirmModal" class="modal-backdrop fade show" style="display: block;"></div>
    <div v-if="showConfirmModal" class="modal fade show" tabindex="-1" style="display: block;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header text-white" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none;">
            <h5 class="modal-title fw-bold">회원가입 최종 확인</h5>
            <button type="button" class="btn-close btn-close-white" @click="cancelConfirm"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning mb-4" role="alert">
              <strong>⚠️ 입력하신 정보가 정확한지 다시 한번 확인하세요!</strong><br>
              <small class="text-muted">아래 정보를 확인하신 후 가입을 완료해주세요.</small>
            </div>

            <div class="table-responsive">
              <table class="table table-borderless mb-0">
                <tbody>
                  <tr>
                    <th class="fw-bold text-secondary" style="width: 30%;">아이디</th>
                    <td class="fw-bold text-dark">{{ confirmData.id }}</td>
                  </tr>
                  <tr>
                    <th class="fw-bold text-secondary">이름</th>
                    <td class="fw-bold text-dark">{{ confirmData.name }}</td>
                  </tr>
                  <tr>
                    <th class="fw-bold text-secondary">전화번호</th>
                    <td class="fw-bold text-dark">{{ confirmData.tel }}</td>
                  </tr>
                  <tr>
                    <th class="fw-bold text-secondary">차량번호</th>
                    <td class="fw-bold text-dark">{{ confirmData.carNumber }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <small class="text-muted d-block mt-3">
              * 비밀번호는 보안상 표시되지 않습니다.
            </small>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="cancelConfirm">
              다시 입력
            </button>
            <button type="button" class="btn btn-dark" @click="confirmRegister" :disabled="loading">
              {{ loading ? '처리 중...' : '가입 완료' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RegisterView',
  data() {
    return {
      form: { id: '', password: '', passwordConfirm: '', name: '', tel: '', carNumber: '' },
      loading: false,
      error: null,
      success: null,
      // 최종 확인 모달
      showConfirmModal: false,
      confirmData: {},
      // 본인인증 관련
      phoneVerified: false,
      authSending: false,
      showAuthInput: false,
      authSent: false,
      generatedAuthCode: '',
      inputAuthCode: '',
      authError: '',
      authSuccess: false,
      authStartTime: null,
      authTimeRemaining: 0,
      authTimer: null,
      resendRemaining: 0,
      resendTimer: null
    }
  },
  methods: {
    formatPhoneDisplay(v) {
      if (!v) return ''
      const d = v.replace(/\D/g, '')
      if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      if (d.length === 10) return d.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      return v
    },
    getPhoneNumberDigitsOnly() {
      return this.form.tel.replace(/\D/g, '')
    },
    async requestAuthCode() {
      if (this.authSending) return
      this.authError = ''
      this.authSuccess = false
      
      const phoneDigits = this.getPhoneNumberDigitsOnly()
      if (!phoneDigits || phoneDigits.length < 10) {
        this.authError = '휴대폰 번호를 정확히 입력하세요.'
        return
      }
      
      // 6자리 인증번호 생성
      this.generatedAuthCode = Math.floor(100000 + Math.random() * 900000).toString()
      this.authSending = true
      
      try {
        const msgContent = `[은평구시설관리공단] 인증번호: ${this.generatedAuthCode}`
        const res = await fetch(`${process.env.VUE_APP_API_BASE}/api/ebpp/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phoneNumber: phoneDigits,
            msg: msgContent,
            reqType: '01'
          })
        })
        
        const result = await res.json()
        if (result.success) {
          this.showAuthInput = true
          this.authSent = true
          this.inputAuthCode = ''
          this.authError = ''
          
          // 3분(180초) 타이머 시작
          this.authStartTime = Date.now()
          this.authTimeRemaining = 180
          
          // 재전송 대기 60초
          this.resendRemaining = 60
          
          if (this.authTimer) clearInterval(this.authTimer)
          if (this.resendTimer) clearInterval(this.resendTimer)
          
          // 인증 시간 타이머
          this.authTimer = setInterval(() => {
            this.authTimeRemaining--
            if (this.authTimeRemaining <= 0) {
              clearInterval(this.authTimer)
              this.authTimer = null
            }
          }, 1000)
          
          // 재전송 대기 타이머
          this.resendTimer = setInterval(() => {
            this.resendRemaining--
            if (this.resendRemaining <= 0) {
              clearInterval(this.resendTimer)
              this.resendTimer = null
            }
          }, 1000)
        } else {
          this.authError = '인증번호 전송 실패: ' + (result.message || '알 수 없는 오류')
        }
      } catch (e) {
        this.authError = '인증번호 전송 중 오류가 발생했습니다: ' + (e.message || '')
      } finally {
        this.authSending = false
      }
    },
    
    verifyAuthCode() {
      this.authError = ''
      
      if (this.authTimeRemaining <= 0) {
        this.authError = '인증 제한시간이 초과되었습니다. 다시 요청해주세요.'
        return
      }
      
      if (this.inputAuthCode === this.generatedAuthCode) {
        this.phoneVerified = true
        this.authSuccess = true
        this.showAuthInput = false
        this.authSent = false
        
        if (this.authTimer) {
          clearInterval(this.authTimer)
          this.authTimer = null
        }
        if (this.resendTimer) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      } else {
        this.authError = '인증번호가 일치하지 않습니다.'
      }
    },
    
    async register() {
      this.error = null
      this.success = null
      
      // 본인인증 완료 여부 확인
      if (!this.phoneVerified) {
        this.error = '본인인증이 필요합니다.'
        return
      }
      
      const { id, password, passwordConfirm, name, tel, carNumber } = this.form
      
      // 로컬 벨리데이션
      if (!id || !password || !name || !tel || !carNumber) {
        this.error = '아이디, 비밀번호, 이름, 전화번호, 차량번호는 필수 입력입니다.'
        return
      }
      if (String(id).length < 4) {
        this.error = '아이디는 최소 4자 이상이어야 합니다.'
        return
      }
      if (String(password).length < 4) {
        this.error = '비밀번호는 최소 4자 이상이어야 합니다.'
        return
      }
      if (password !== passwordConfirm) {
        this.error = '비밀번호가 일치하지 않습니다.'
        return
      }
      
      // 중복 검사 API 호출
      this.loading = true
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_BASE}/api/validate-register`, {
          id,
          tel: this.formatPhoneDisplay(tel),
          carNumber
        })
        
        // 검증 통과 시 최종 확인 모달 표시
        if (response.data.success) {
          this.confirmData = {
            id,
            name,
            tel: this.formatPhoneDisplay(tel),
            carNumber
          }
          this.showConfirmModal = true
        }
      } catch (e) {
        // 중복 정보 오류
        this.error = e.response?.data?.message || '회원가입 검증에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },

    cancelConfirm() {
      this.showConfirmModal = false
      this.error = null
    },

    async confirmRegister() {
      this.loading = true
      try {
        const { id, password, name, tel, carNumber } = this.form
        const formattedTel = this.formatPhoneDisplay(tel)
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/register`, { id, password, name, tel: formattedTel, carNumber })
        this.success = '회원가입이 완료되었습니다. 로그인 페이지로 이동합니다.'
        this.showConfirmModal = false
        setTimeout(() => this.$router.push('/login'), 1500)
      } catch (e) {
        this.error = e.response?.data?.message || '회원가입에 실패했습니다.'
      } finally {
        this.loading = false
      }
    }
  },
  beforeUnmount() {
    // 컴포넌트 소멸 시 타이머 정리
    if (this.authTimer) clearInterval(this.authTimer)
    if (this.resendTimer) clearInterval(this.resendTimer)
  }
}
</script>
